import { Localized, useLocalization } from "@fluent/react";
import { Button } from "@narmi/design_system";
import * as Sentry from "@sentry/react";
import { addDays } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { ErrorScreen } from "@lending/components/ErrorScreen/ErrorScreen";
import { PageLayout } from "@lending/components/PageLayout/PageLayout";
import { SimpleSlider } from "@lending/components/SimpleSlider/SimpleSlider";
import { calculateMonthlyPayment } from "@lending/pages/PersonalLoan/Offers/utils/loanCalculations";
import {
  formatCentsAsDollars,
  formatCentsAsDollarsAndCents,
  formatShortDate,
} from "@lending/utils/formatters";
import styles from "./SliderOffers.module.css";
import { usePersonalLoanOffers } from "../hooks/usePersonalLoanOffers";

function SliderOffers() {
  const { l10n } = useLocalization();
  const {
    application,
    coApplicantFirstName,
    displayAmountCents,
    hasOnlyLowerAmountOffers,
    institution,
    institutionName,
    isLoading,
    isPending,
    onSubmit,
    personalLoanOffers,
    principal,
    primaryApplicantFirstName,
    shouldLogMissingOffers,
    shouldShowNoOffersError,
    variables,
  } = usePersonalLoanOffers();
  const minimumTermMonths = Math.max(
    Number(institution.lending_personal_loan_min_term_months ?? 1),
    1
  );
  const stepMonths = institution.lending_personal_loan_term_step_months;
  const sliderOffers = useMemo(() => {
    // Fan each Alloy offer out into one selectable slider stop per allowed
    // term. Each offer is (offered_amount, apr, term_months) where
    // term_months is the *maximum* term at that APR; we stride from
    // `minimumOfferTermMonths` up to `offer.term_months` in `stepMonths`
    // increments so applicants can pick shorter terms priced at the same
    // APR. Each offer after the first starts one step past the previous
    // offer's max so tiers do not overlap; the first offer starts at the
    // institution minimum (clamped to that offer's own max).
    //
    // Alloy is expected to return offers whose term_months sit on the
    // configured step boundaries; if an offer's max is not reachable in
    // whole steps from its start, the excess months are dropped rather
    // than emitting an off-step stop.
    let previousMaximumTermMonths = 0;

    return personalLoanOffers.flatMap((offer) => {
      const minimumOfferTermMonths = previousMaximumTermMonths
        ? previousMaximumTermMonths + stepMonths
        : Math.min(minimumTermMonths, offer.term_months);

      previousMaximumTermMonths = offer.term_months;

      const stepCount = Math.max(
        Math.floor((offer.term_months - minimumOfferTermMonths) / stepMonths) + 1,
        0
      );

      return Array.from({ length: stepCount }, (_, index) => {
        const termMonths = minimumOfferTermMonths + index * stepMonths;

        return {
          ...offer,
          monthly_payment: calculateMonthlyPayment(principal, offer.apr_basis / 100, termMonths),
          term_months: termMonths,
        };
      });
    });
  }, [minimumTermMonths, personalLoanOffers, principal, stepMonths]);
  const [selectedOfferIndex, setSelectedOfferIndex] = useState(0);
  const ctaLabel = l10n.getString("offer-card-submit", null, "Get started");
  const infoLinkUrl =
    institution.lending_personal_loan_offer_info_and_disclaimer_url ||
    institution.lending_offer_info_and_disclaimer_url;

  useEffect(() => {
    const nextSelectedOfferIndex = Math.max(sliderOffers.length - 1, 0);
    setSelectedOfferIndex((currentIndex) =>
      currentIndex === nextSelectedOfferIndex ? currentIndex : nextSelectedOfferIndex
    );
  }, [sliderOffers.length]);

  const selectedOffer = sliderOffers[selectedOfferIndex] ?? null;
  const expirationDate =
    selectedOffer &&
    formatShortDate(
      addDays(
        new Date(selectedOffer.created_at),
        institution.lending_application_offers_expiration_days
      )
    );

  useEffect(() => {
    if (shouldLogMissingOffers) {
      Sentry.captureMessage(
        "A user navigated to the personal loan offer screen but did not have an offer. This should not happen."
      );
    }
  }, [shouldLogMissingOffers]);

  if (shouldShowNoOffersError) {
    return <ErrorScreen />;
  }

  return (
    <PageLayout
      isLoading={isLoading || application == null}
      style={{ paddingTop: "var(--space-xxl)" }}
    >
      <div className={styles.contentWrapper}>
        <div className={styles.content}>
          <div className={styles.offerDetails}>
            {selectedOffer && (
              <div className={styles.offerCard}>
                <div className={styles.header}>
                  <div className={styles.kicker}>
                    {"🎉 "}
                    {coApplicantFirstName
                      ? l10n.getString(
                          "offers-heading-joint-slider",
                          {
                            firstName: primaryApplicantFirstName,
                            secondName: coApplicantFirstName,
                          },
                          `Good news, ${primaryApplicantFirstName} and ${coApplicantFirstName}`
                        )
                      : l10n.getString(
                          "offers-heading-slider",
                          { firstName: primaryApplicantFirstName },
                          `Good news, ${primaryApplicantFirstName}`
                        )}
                  </div>
                  <div className={`fontColor--heading fontFamily--default ${styles.offerTitle}`}>
                    {hasOnlyLowerAmountOffers
                      ? l10n.getString(
                          "offers-subheading-slider-lower-than-requested",
                          null,
                          "We can offer you up to"
                        )
                      : l10n.getString(
                          "offers-subheading-slider",
                          null,
                          "You're prequalified for"
                        )}{" "}
                    <span className={`fontColor--heading fontFamily--default ${styles.maxOffer}`}>
                      {formatCentsAsDollars(displayAmountCents)}
                    </span>
                  </div>
                </div>
                <div className={styles.divider} />
                <div className={styles.monthlyPaymentSection}>
                  <div className={styles.monthlyPaymentLabel}>
                    {l10n.getString("offer-card-monthly-payment", null, "Monthly payment")}
                  </div>
                  <div className={styles.monthlyPaymentAmount}>
                    {formatCentsAsDollarsAndCents(selectedOffer.monthly_payment)}
                  </div>
                  <div className={styles.offerMeta}>
                    <span>
                      {l10n.getString(
                        "offers-selected-term",
                        { termMonths: selectedOffer.term_months },
                        `${selectedOffer.term_months} months`
                      )}
                    </span>
                    <span className={styles.offerMetaDivider}>·</span>
                    <span>
                      {l10n.getString(
                        "offers-selected-apr",
                        { apr: (selectedOffer.apr_basis / 100).toFixed(2) },
                        `${(selectedOffer.apr_basis / 100).toFixed(2)}% APR`
                      )}
                    </span>
                  </div>
                </div>
                <div className={styles.divider} />
                <div className={styles.sliderSection}>
                  <div className={styles.sliderHeader}>
                    <span className={styles.sliderTitle}>
                      {l10n.getString("offers-slider-label", null, "Pay back over")}
                    </span>
                    <span className={styles.termPill}>
                      {l10n.getString(
                        "offers-slider-pill",
                        { termMonths: selectedOffer.term_months },
                        `${selectedOffer.term_months} months`
                      )}
                    </span>
                  </div>
                  <SimpleSlider
                    min={0}
                    max={Math.max(sliderOffers.length - 1, 0)}
                    step={1}
                    value={selectedOfferIndex}
                    setValue={setSelectedOfferIndex}
                  />
                  <div className={styles.sliderRangeLabels}>
                    <span>
                      {l10n.getString(
                        "offers-slider-min-label",
                        { termMonths: sliderOffers[0]?.term_months ?? 0 },
                        `${sliderOffers[0]?.term_months ?? 0} mo`
                      )}
                    </span>
                    <span>
                      {l10n.getString(
                        "offers-slider-max-label",
                        { termMonths: sliderOffers[sliderOffers.length - 1]?.term_months ?? 0 },
                        `${sliderOffers[sliderOffers.length - 1]?.term_months ?? 0} mo`
                      )}
                    </span>
                  </div>
                </div>
                <Button
                  label={ctaLabel}
                  type="submit"
                  endIcon="arrow-right"
                  onClick={() => onSubmit(selectedOffer)}
                  isLoading={isPending}
                  disabled={isPending && variables?.body?.product_offer !== selectedOffer.id}
                  style={{ width: "100%" }}
                />
                <div className={styles.cardFooter}>
                  <span>
                    {l10n.getString(
                      "offers-expiration-label",
                      { date: expirationDate },
                      `Offer expires ${expirationDate}`
                    )}
                  </span>
                  <span className={styles.offerMetaDivider}>·</span>
                  <Button
                    as="a"
                    kind="plain"
                    label={l10n.getString(
                      "offer-card-disclosures-link-text",
                      null,
                      "View disclosures"
                    )}
                    size="xs"
                    onClick={() => {
                      window.open(infoLinkUrl, "_blank", "noopener, noreferrer");
                    }}
                  />
                </div>
              </div>
            )}
          </div>
          <div className={styles.footer}>
            <div className="fontSize--s fontWeight--thin">
              <Localized
                id="offers-consent-disclaimer-personal"
                vars={{ ctaLabel, institutionName }}
              >
                {`By clicking "${ctaLabel}" above, you consent to authorize ${institutionName} to obtain and use Consumer Reports about you to review your application, to review the products or services you maintain, and to analyze your financial needs and to market other products and services to you.`}
              </Localized>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
}

export { SliderOffers };
